/**
 * 问卷枚举值中文映射工具
 * 统一管理所有问卷字段的枚举值到中文的映射
 */

// 企业类型映射
export const getEnterpriseTypeText = (type: string) => {
  const map: Record<string, string> = {
    central_soe: '中央企业',
    local_soe: '地方国有企业',
    local_soe_city_investment: '地方国有企业（城投企业）',
    listed_company: '上市公司',
    private_enterprise: '民营企业',
    private: '民营企业',
    foreign_enterprise: '外资企业',
    foreign: '外资企业',
    joint_venture: '合资企业',
    other: '其他',
  };
  return map[type] || type;
};

// 注册地映射
export const getRegistrationLocationText = (location: string) => {
  const map = {
    mainland_china: '中国境内',
    overseas: '中国境外（含港澳台）',
    // 兼容旧数据
    hong_kong: '中国境外（含港澳台）',
    macau: '中国境外（含港澳台）',
    taiwan: '中国境外（含港澳台）',
    other_overseas: '中国境外（含港澳台）',
  };
  return map[location as keyof typeof map] || location;
};

// 信用评级映射
export const getCreditRatingText = (rating: string) => {
  const map: Record<string, string> = {
    AAA: 'AAA级',
    'AA+': 'AA+级',
    AA: 'AA级',
    'AA-': 'AA-级',
    'A+': 'A+级',
    A: 'A级',
    'A-': 'A-级',
    'A+_and_below': 'A+及以下',
    'BBB+': 'BBB+级',
    BBB: 'BBB级',
    'BBB-': 'BBB-级',
    'BB+': 'BB+级',
    BB: 'BB级',
    'BB-': 'BB-级',
    'B+': 'B+级',
    B: 'B级',
    'B-': 'B-级',
    CCC: 'CCC级',
    CC: 'CC级',
    C: 'C级',
    D: 'D级',
    no_rating: '无评级',
  };
  return map[rating] || rating;
};

// 国际评级映射
export const getInternationalRatingText = (rating: string) => {
  const map: Record<string, string> = {
    AAA: 'AAA级',
    'AA+': 'AA+级',
    AA: 'AA级',
    'AA-': 'AA-级',
    'A+': 'A+级',
    A: 'A级',
    'A-': 'A-级',
    'BBB+': 'BBB+级',
    BBB: 'BBB级',
    'BBB-': 'BBB-级',
    'BB+': 'BB+级',
    BB: 'BB级',
    'BB-': 'BB-级',
    'B+': 'B+级',
    B: 'B级',
    'B-': 'B-级',
    CCC: 'CCC级',
    CC: 'CC级',
    C: 'C级',
    D: 'D级',
    no_international_rating: '无国际评级',
    // Bug修复: 新增英文枚举值映射
    investment_grade: '投资级',
    non_investment_grade: '非投资级',
    speculative_grade: '投机级',
    not_rated: '未评级',
  };
  return map[rating] || rating;
};

// 总资产规模映射（带千分位逗号）
export const getTotalAssetsText = (assets: string) => {
  const map: Record<string, string> = {
    above_100b: '1,000亿元以上',
    '50b_to_100b': '500-1,000亿元',
    from_50b_to_100b: '500-1,000亿元',
    '20b_to_50b': '200-500亿元',
    from_20b_to_50b: '200-500亿元',
    '10b_to_20b': '100-200亿元',
    from_10b_to_20b: '100-200亿元',
    '5b_to_10b': '50-100亿元',
    from_5b_to_10b: '50-100亿元',
    below_5b: '50亿元以下',
  };
  return map[assets] || assets;
};

// 行业映射
export const getIndustryText = (industry: string) => {
  const map: Record<string, string> = {
    energy: '能源',
    infrastructure: '基础设施',
    financial_services: '金融服务',
    finance: '金融',
    real_estate: '房地产',
    manufacturing: '制造业',
    consumer_services: '消费服务',
    consumer: '消费',
    technology: '科技',
    transportation: '交通运输',
    utilities: '公用事业',
    healthcare: '医疗健康',
    materials: '原材料',
    telecommunications: '电信',
    other: '其他',
  };
  return map[industry] || industry;
};

// 审计意见映射
export const getAuditOpinionText = (opinion: string) => {
  const map = {
    unqualified: '无保留意见',
    qualified: '保留意见',
    adverse: '否定意见',
    disclaimer: '无法表示意见',
  };
  return map[opinion as keyof typeof map] || opinion;
};

// 绿色项目状态映射
export const getGreenProjectStatusText = (status: string) => {
  const map: Record<string, string> = {
    clear_project: '有明确项目',
    in_planning: '规划中',
    no_clear_project: '无明确项目',
    planning_stage: '规划阶段',
    uncertain: '不确定',
    yes: '有',
    no: '无',
  };
  return map[status] || status;
};

// 项目类别映射
export const getProjectCategoryText = (category: string) => {
  const map: Record<string, string> = {
    renewable_energy: '可再生能源',
    energy_saving: '节能减排',
    environmental_treatment: '环境治理',
    clean_transportation: '清洁交通',
    green_building: '绿色建筑',
    circular_economy: '循环经济',
    ecological_protection: '生态保护',
    other_green: '其他绿色项目',
    // 兼容旧值
    energy_efficiency: '能源效率',
    pollution_prevention: '污染防治',
    sustainable_water: '可持续水资源管理',
    climate_adaptation: '气候变化适应',
    eco_efficient_products: '生态高效产品',
    green_buildings: '绿色建筑',
  };
  return map[category] || category;
};

// 投资规模映射
export const getInvestmentScaleText = (scale: string) => {
  const map: Record<string, string> = {
    above_10b: '100亿元以上',
    '5b_to_10b': '50-100亿元',
    from_5b_to_10b: '50-100亿元',
    '2b_to_5b': '20-50亿元',
    '1b_to_5b': '10-50亿元',
    from_2b_to_5b: '20-50亿元',
    '1b_to_2b': '10-20亿元',
    from_1b_to_2b: '10-20亿元',
    '500m_to_1b': '5-10亿元',
    from_500m_to_1b: '5-10亿元',
    below_500m: '5亿元以下',
  };
  return map[scale] || scale;
};

// 绿色标准符合性映射
export const getGreenStandardComplianceText = (compliance: string) => {
  const map: Record<string, string> = {
    certified: '已获得权威机构绿色认证',
    pboc_compliant: '符合央行绿色金融目录',
    international_compliant: '符合国际绿色标准',
    basically_compliant: '基本符合需确认',
    uncertain: '不确定是否符合',
    // 兼容旧值
    fully_compliant: '完全符合',
    partially_compliant: '部分符合',
    non_compliant: '不符合',
  };
  return map[compliance] || compliance;
};

// 发行规模映射
export const getIssuanceScaleText = (scale: string) => {
  const map: Record<string, string> = {
    above_5b: '50亿元以上',
    '2b_to_5b': '20-50亿元',
    '1b_to_2b': '10-20亿元',
    '500m_to_1b': '5-10亿元',
    '300m_to_500m': '3-5亿元',
    below_300m: '3亿元以下',
    // 兼容旧值
    '3b_to_5b': '30-50亿元',
    '1b_to_3b': '10-30亿元',
    below_500m: '5亿元以下',
    above_1b: '10亿元以上',
    from_500m_to_1b: '5-10亿元',
    from_300m_to_500m: '3-5亿元',
    from_2b_to_5b: '20-50亿元',
    from_1b_to_2b: '10-20亿元',
  };
  return map[scale] || scale;
};

// 发行时间映射
export const getIssuanceTimeText = (time: string) => {
  const map: Record<string, string> = {
    within_1_month: '1个月内',
    within_3_months: '3个月内',
    within_6_months: '6个月内',
    within_1_year: '1年内',
    over_1_year: '1年以上',
    beyond_1_year: '1年以上',
    above_1_year: '1年以上',
    uncertain: '尚不确定',
  };
  return map[time] || time;
};

// 产品类型映射
export const getProductTypeText = (type: string) => {
  const map: Record<string, string> = {
    // 当前前端枚举值（frontend/src/types/questionnaire.ts ProductType）
    public_green_bond: '公开发行绿色公司债',
    private_green_bond: '私募绿色债券',
    green_mtn: '绿色中期票据',
    green_syndicated_loan: '绿色银团贷款',
    green_abs_reits: '绿色ABS/REITs',
    other_green_product: '其他绿色融资产品',
    // 兼容旧值
    green_bond: '绿色债券',
    sustainability_bond: '可持续发展债券',
    transition_bond: '转型债券',
    blue_bond: '蓝色债券',
    other: '其他',
    green_corporate_bond: '绿色企业债',
    green_medium_term_note: '绿色中期票据',
    green_short_term_financing: '绿色短期融资券',
    green_asset_backed_securities: '绿色资产支持证券',
  };
  return map[type] || type;
};

// 目标市场映射
export const getTargetMarketText = (market: string) => {
  const map: Record<string, string> = {
    domestic: '境内市场',
    overseas: '境外市场',
    offshore: '离岸市场',
    both: '境内外市场',
    undecided: '尚未确定',
    // 兼容旧值
    domestic_only: '境内市场',
    overseas_only: '境外市场',
    flexible: '灵活选择',
    macau: '澳门市场',
  };
  return map[market] || market;
};

// 风险因素映射
export const getRiskFactorText = (factor: string) => {
  const map: Record<string, string> = {
    // 诉讼状态 - 与 QuestionnaireStep4 组件中的标签保持一致
    no_litigation: '无重大诉讼或仲裁',
    no_major_litigation: '无重大诉讼',
    minor_litigation: '有诉讼但金额较小',
    ongoing_litigation: '有正在进行的诉讼',
    major_litigation: '有重大诉讼风险',
    has_major_litigation_resolved: '有轻微诉讼已解决',
    major_litigation_high_win_rate: '有重大诉讼但胜诉概率高',
    major_litigation_high_loss_risk: '存在重大败诉风险',
    // 环保合规 - 与 QuestionnaireStep4 组件中的标签保持一致
    fully_compliant: '完全合规',
    good_compliance: '环保合规良好，无违规记录',
    minor_issues: '轻微问题已整改',
    minor_issues_resolved: '有轻微环保问题但已整改',
    under_rectification: '正在整改中',
    serious_violations: '存在严重违规',
    minor_violations: '轻微违规',
    major_violations: '重大违规',
    violations_being_handled: '存在环保违规正在处理',
    major_violation_risk: '有重大环保违规风险',
    // 监管审批 - 与 QuestionnaireStep4 组件中的标签保持一致
    approved: '已获得批准',
    all_approvals_obtained: '已获得所有必要批文',
    most_approvals_obtained: '大部分批文已获得',
    applying_key_approvals: '正在申请关键批文',
    pending_approvals: '审批进行中',
    no_approvals_required: '无需审批',
    in_progress: '正在申请中',
    not_required: '不需要特殊批准',
    uncertain: '不确定是否需要',
    under_review: '审批中',
    not_started: '尚未开始审批流程',
    rejected: '被拒绝',
  };
  return map[factor] || factor;
};

// 紧急程度映射 - 与 QuestionnaireStep4 组件中的标签保持一致
export const getUrgencyLevelText = (level: string) => {
  const map: Record<string, string> = {
    very_urgent: '非常紧急（希望24小时内联系）',
    urgent: '比较紧急（希望3天内联系）',
    normal: '一般紧急（希望1周内联系）',
    not_urgent: '不急（有合适机会再联系）',
    // 兼容旧值
    high: '高',
    medium: '中',
    low: '低',
  };
  return map[level] || level;
};

// 问卷状态映射
export const getQuestionnaireStatusText = (status: string) => {
  const map = {
    draft: '草稿',
    submitted: '已提交',
    under_review: '审核中',
    approved: '已通过',
    rejected: '被驳回',
    need_supplement: '需补充',
  };
  return map[status as keyof typeof map] || status;
};

// 风险等级映射
export const getRiskLevelText = (riskLevel: string) => {
  const map = {
    low: '低风险',
    medium: '中风险',
    high: '高风险',
  };
  return map[riskLevel as keyof typeof map] || riskLevel;
};

// ⭐ 新增：财务状况相关映射

// 资产负债率映射
export const getDebtToAssetRatioText = (ratio: string) => {
  const map: Record<string, string> = {
    below_60: '60%以下',
    '60_to_70': '60%-70%',
    '70_to_80': '70%-80%',
    above_80: '80%以上',
    unknown: '不清楚',
    // 兼容旧值
    below_30: '30%以下',
    from_30_to_50: '30%-50%',
    from_50_to_60: '50%-60%',
    from_60_to_70: '60%-70%',
    above_70: '70%以上',
  };
  return map[ratio] || ratio;
};

// 净利润规模映射
export const getNetProfitText = (profit: string) => {
  const map: Record<string, string> = {
    above_1b: '10亿元以上',
    '500m_to_1b': '5-10亿元',
    '100m_to_500m': '1-5亿元',
    '0_to_100m': '0-1亿元',
    loss: '亏损',
    // 兼容旧值
    above_10b: '100亿元以上',
    from_5b_to_10b: '50-100亿元',
    from_1b_to_5b: '10-50亿元',
    from_500m_to_1b: '5-10亿元',
    from_100m_to_500m: '1-5亿元',
    below_100m: '1亿元以下',
  };
  return map[profit] || profit;
};

// 现金流状况映射
export const getCashFlowStatusText = (status: string) => {
  const map: Record<string, string> = {
    excellent: '优秀',
    good: '良好',
    fair: '一般',
    poor: '较差',
    very_poor: '很差',
    unknown: '不清楚',
  };
  return map[status] || status;
};

// 债务违约记录映射 - 与 QuestionnaireStep2 组件中的标签保持一致
export const getDefaultRecordText = (record: string) => {
  const map: Record<string, string> = {
    no_default: '无任何违约记录',
    minor_resolved: '有违约记录',
    default_restructured: '违约已重组',
    current_default: '目前存在违约',
    // 兼容旧值
    minor_default: '轻微违约',
    major_default: '重大违约',
    frequent_default: '频繁违约',
  };
  return map[record] || record;
};

// 绿色项目状态映射（补充）
export const getHasGreenProjectText = (status: string) => {
  const map = {
    yes: '有明确绿色项目',
    no: '无明确绿色项目',
    planning: '规划中',
    uncertain: '不确定',
  };
  return map[status as keyof typeof map] || status;
};

// 注册地映射（补充更多选项）
export const getRegistrationLocationTextExtended = (location: string) => {
  const map = {
    beijing: '北京',
    shanghai: '上海',
    guangzhou: '广州',
    shenzhen: '深圳',
    tianjin: '天津',
    chongqing: '重庆',
    hangzhou: '杭州',
    nanjing: '南京',
    wuhan: '武汉',
    chengdu: '成都',
    xian: '西安',
    shenyang: '沈阳',
    dalian: '大连',
    qingdao: '青岛',
    jinan: '济南',
    zhengzhou: '郑州',
    changsha: '长沙',
    nanchang: '南昌',
    fuzhou: '福州',
    xiamen: '厦门',
    kunming: '昆明',
    guiyang: '贵阳',
    nanning: '南宁',
    haikou: '海口',
    lanzhou: '兰州',
    yinchuan: '银川',
    xining: '西宁',
    urumqi: '乌鲁木齐',
    lhasa: '拉萨',
    hohhot: '呼和浩特',
    harbin: '哈尔滨',
    changchun: '长春',
    shijiazhuang: '石家庄',
    taiyuan: '太原',
    hefei: '合肥',
    mainland_china: '中国大陆',
    hong_kong: '香港',
    macau: '澳门',
    taiwan: '台湾',
    overseas: '中国境外（含港澳台）',
    other_overseas: '其他境外地区',
  };
  return map[location as keyof typeof map] || location;
};

// 紧急程度映射（补充）- 与 QuestionnaireStep4 组件中的标签保持一致
export const getUrgencyLevelTextExtended = (level: string) => {
  const map: Record<string, string> = {
    very_urgent: '非常紧急（希望24小时内联系）',
    urgent: '比较紧急（希望3天内联系）',
    normal: '一般紧急（希望1周内联系）',
    not_urgent: '不急（有合适机会再联系）',
    // 兼容旧值
    high: '高',
    medium: '中',
    low: '不急',
  };
  return map[level] || level;
};

// 企业类型映射（补充）- 必须包含所有可能的枚举值
export const getEnterpriseTypeTextExtended = (type: string) => {
  const map: Record<string, string> = {
    // 当前系统使用的枚举值
    central_soe: '中央企业',
    local_soe: '地方国有企业',
    local_soe_city_investment: '地方国有企业（城投企业）',
    listed_company: '上市公司',
    private_enterprise: '民营企业',
    private: '民营企业',
    foreign_enterprise: '外资企业',
    foreign: '外资企业',
    joint_venture: '合资企业',
    other: '其他',
  };
  return map[type] || type;
};

// 目标市场映射（补充）
export const getTargetMarketTextExtended = (market: string) => {
  const map: Record<string, string> = {
    domestic: '境内市场',
    domestic_only: '境内市场',
    overseas: '境外市场',
    overseas_only: '境外市场',
    offshore: '离岸市场',
    both: '境内外市场',
    flexible: '灵活选择',
    macau: '澳门市场',
    undecided: '尚未确定',
  };
  return map[market] || market;
};

/**
 * Bug修复: 格式化项目/问卷ID为统一的短ID格式
 * 将长ID（如 questionnaire_1704123456789_abc123def）转换为短ID（如 GB-ABC123DEF）
 * @param id 原始ID
 * @returns 格式化后的短ID
 */
export const formatProjectId = (id: string): string => {
  if (!id) return '-';

  // 如果ID包含下划线，提取最后一个部分
  const parts = id.split('_');
  if (parts.length >= 3) {
    // 格式：prefix_timestamp_random -> 取random部分
    const shortId = parts[parts.length - 1].toUpperCase();
    return `GB-${shortId}`;
  }

  // 如果ID很长（超过12位），截取后8位
  if (id.length > 12) {
    return `GB-${id.slice(-8).toUpperCase()}`;
  }

  // 否则直接返回
  return id;
};

/**
 * 获取完整ID的tooltip显示文本
 * @param id 原始ID
 * @returns 用于tooltip的完整ID
 */
export const getFullProjectIdTooltip = (id: string): string => {
  if (!id) return '';
  return `完整ID: ${id}`;
};
